/**
 * vocabSeeds.js — Canonical `definition_home` for centrally-managed vocab sets.
 *
 * ============================================================================
 * ADR / RULE (PR-B1 2026-07-21) — READ THIS BEFORE EDITING
 * ============================================================================
 *
 * This file is the **SINGLE canonical `definition_home`** for every centrally-
 * managed vocab / enum / taxonomy in the Bidit frontend. Every consumer must
 * read from here — directly, or via `useSettings` (getOptions / getEnumLabel),
 * or (once PR-B2.B.6 lands) via the embedded `vocabSeeds.json` on the backend.
 *
 * **Adding a shadow copy of any of these sets — a `_FALLBACK` map, a local
 * DEFAULTS constant, a hardcoded array — is a merge block.** If you need a
 * value that isn't here, add it here first, then bind your consumer. If your
 * consumer needs to fall back when a value is unresolved, use
 * `useSettings().getEnumLabel(listType, value, { quoteDefaultsField })` which
 * implements the canonical 4-step precedence chain:
 *
 *   1. Live settings list match (tenant-scoped, editable via Settings pages)
 *   2. This file's canonical seed match
 *   3. Tenant's Quote Defaults value for the field (if caller supplies
 *      `quoteDefaultsField`, e.g., `'payment_type'` → falls back to the tenant
 *      default when the actual quote's payment_type is unresolved)
 *   4. Cosmetic `prettifyEnum(rawValue)` (snake_case → Title Case)
 *
 * Cross-references:
 * - PR-B1 execution plan: context/plans/tech-debt-cleanup-post-pr4.md §2 PR-B1
 * - Prior authority (superseded): VOCAB_SEEDS.md v1.3 (2026-07-06 / R17-07)
 * - Rule N-13 (status enums, prior art): status tones require `status_set_id`
 *   + completeness; ADR-00X (PR-D.1, forthcoming) will generalize N-13 to all
 *   seeded sets and this file's shape is that ADR's target.
 *
 * Shape:
 *   VOCAB_SEEDS = {
 *     [setKey]: {
 *       set_id:    stable identifier, e.g. 'payment_type.v1'
 *       custom:    boolean — may operators add rows via Settings? (see rule below)
 *       values:    Array<string | {value, label, active?, description?, behavior?, ...metadata}>
 *                  Some sets (payment_term, payment_type, user_role) extend each row
 *                  with a `description` (operator-visible helper text on Settings +
 *                  optional tooltip on customer-facing surfaces) and a `behavior`
 *                  object encoding the automation semantics of that row.
 *       consumers: Array<string> — every module/component reading this set
 *       note:      free-form provenance / rationale
 *     }
 *   }
 *
 * Behavior schema (PR-B2b-part-1 2026-07-21):
 *   Rows in sets that carry automation logic ship a `behavior: {kind, ...params}`.
 *   The `kind` field is a small closed enum documented per set (see set-level
 *   notes). Consumers switch on `behavior.kind` to route into the right resolver.
 *   The schema exists NOW so downstream code (invoice engine, quote doc, etc.)
 *   can start reading `behavior` when it's ready to implement enforcement —
 *   without another shape migration. Enforcement itself is deferred (see
 *   `context/plans/tech-debt-cleanup-post-pr4.md` §5 "Deferred").
 *
 * `custom` semantics:
 *   - `custom: false` — closed set. Operator CANNOT add rows via Settings.
 *     Changes require a code change to this file + review. Examples: single-
 *     record settings (margin_defaults, quote_defaults), status tones (N-13).
 *   - `custom: true`  — open set. Operator MAY add rows via the Settings page
 *     for this list; unresolved values on rendered surfaces fall back through
 *     the 4-step chain above. Default for all list-type enums.
 *
 * SET KEY vs list_type:
 *   Set keys in this map are SINGULAR (`payment_type`, `customer_type`, etc.)
 *   because they name a single enum. The corresponding list_type used by the
 *   backend + `useSettings` is often PLURAL (`payment_types`, `customer_types`).
 *   `SET_ID_ALIASES` at the bottom of this file maps plural list_type → set key
 *   so both entry points resolve to the same seed.
 *
 * ============================================================================
 */

export const VOCAB_SEEDS_VERSION = '2.4.0'; // IDI-223 2026-10-28 — IDI's starter lists (Lindsey 08-31); 2.3.0 rows kept as prior_seed.
// Was 2.3.0: // PR-B2b-part-3 2026-07-25 — area.v1 promoted from tenant-empty stub to canonical BIDIT-owned set (8 areas, 18 sub-areas nested); sub_area.v1 + quote_area.v1 retired (collapsed into area.v1). Canonical AREAS moved out of quote-builder/seedCatalog.js (definition_home = vocabSeeds.js). Tenants CANNOT add/rename/delete BIDIT areas or sub-areas; tenant-added sub-areas live in settings/sub_areas list (append-only, parent_area_id required).

export const VOCAB_SEEDS = {
  payment_term: {
    set_id: 'payment_term.v1',
    custom: false,
    // behavior.kind enum for payment_term:
    //   'due_date_offset' — invoice due `offset_days` after quote acceptance.
    //                        Params: { offset_days: integer }
    //   'due_on_acceptance' — invoice due immediately at acceptance (COD /
    //                          due-on-receipt semantics). No params.
    //   'milestone_split'  — split into N invoices at named triggers.
    //                        Params: { milestones: [{ trigger, percent }] }
    //                        Known triggers: 'quote_accepted', 'job_started',
    //                        'job_completed', 'punch_list_signed'.
    // IDI-223: `prior_seed` on a set = the 2.3.0 seed rows (the starter rows
    // BIDIT wrote before IDI's lists; on the six name-checked lists, all of
    // them; elsewhere, only those `values` dropped). Never seeded or offered:
    // findSeedLabel still labels them (F3), and the exporter hands their ids
    // and names to the backend reconcile so migrated dealers' lists keep
    // hiding them (F1).
    //
    // IDI-223 (Adrian 10-27): the typical terms. COD, Net 15, Net 30 rows are
    // unchanged (ids are stableId(set, value)). Due on receipt (invoice-only
    // built-in) and 50/50 (no split terms at launch) are retired: they keep
    // their labels (findSeedLabel) but are never seeded.
    values: [
      {
        value: 'cod',
        label: 'COD',
        description: 'Cash on delivery — payment due at time of service.',
        behavior: { kind: 'due_on_acceptance' },
      },
      {
        value: 'net_15',
        label: 'Net 15',
        description: 'Invoice due 15 days after quote acceptance.',
        behavior: { kind: 'due_date_offset', offset_days: 15 },
      },
      {
        value: 'net_30',
        label: 'Net 30',
        description: 'Invoice due 30 days after quote acceptance.',
        behavior: { kind: 'due_date_offset', offset_days: 30 },
      },
    ],
    prior_seed: [
      {
        value: 'due_on_receipt',
        label: 'Due on receipt',
        description: 'Invoice due immediately upon receipt.',
        behavior: { kind: 'due_on_acceptance' },
      },
      {
        value: '50_50',
        label: '50% down / 50% on completion',
        description: '50% due at quote acceptance; 50% due at job completion. This term prints on the customer quote — the explicit wording is intentional (operator ruling §3c 2026-07-21).',
        behavior: {
          kind: 'milestone_split',
          milestones: [
            { trigger: 'quote_accepted', percent: 50 },
            { trigger: 'job_completed',  percent: 50 },
          ],
        },
      },
    ],
    consumers: ['CustomerFormPanel.js', 'QuoteBuilder', 'QuoteReviewSend.js', 'QuoteDefaultsPage.js', '(future) InvoiceEngine'],
    note: 'Operator ruling Q1 (2026-07-21). PR-B2a: flipped custom:true → custom:false. PR-B2b-part-1: extended each row with `description` (super-admin helper text) + `behavior: {kind, params}` schema that the future InvoiceEngine will consume to derive due dates + milestone splits. Enforcement logic is not shipped yet — the schema locks the shape so downstream code can start reading it without another migration.',
  },

  invoice_status: {
    set_id: 'invoice_status.v1',
    custom: false,
    // Values align with backend `types.go` `InvoiceStatus` enum + all existing
    // frontend consumers (InvoiceDetail, InvoicesPage, integration tests).
    // Labels align with IN-1 §207 vocabulary — the user-facing word for the
    // not-yet-sent state is "Unsent" (draftness is an internal detail).
    //
    // `void` is REGISTER-ONLY (operator ruling 2026-07-26): the seed contract
    // includes it because `deriveAging` reads it, but no producer exists on
    // the backend yet (state machine tops out at Paid). Building a Void
    // filter chip or Void action affordance today would be against an
    // unreachable state — do not add one until backend `InvoiceStatusVoid` +
    // transition rules land (TD filed 2026-07-26).
    values: [
      {
        value: 'draft',
        label: 'Unsent',
        description: 'Invoice created but not yet sent to the customer.',
      },
      {
        value: 'sent',
        label: 'Sent',
        description: 'Invoice sent to the customer; awaiting payment.',
      },
      {
        value: 'paid',
        label: 'Paid',
        description: 'Invoice fully paid.',
      },
      {
        value: 'void',
        label: 'Void',
        description: 'Invoice voided (terminal, non-recoverable). REGISTER-ONLY in PT-23 — no backend producer yet; do not build UI affordances until Void transition ships.',
      },
    ],
    consumers: ['InvoicesTab.js', 'InvoiceDetail.js', 'invoiceLifecycle.deriveAging'],
    note: 'PT-23 (2026-07-26): registered per master plan `pt-17-pt-26-jobs-wos-invoices-zero-miss-build.md` §3 box 23.2. Value keys align with backend `types.go` (draft/sent/paid) + register-only `void`; labels align with IN-1 §207 vocabulary. `deriveAging` returns `Unsent` for the `draft` state — one vocabulary across list (aging pill) and detail (base-status pill via getEnumLabel).',
  },

  payment_type: {
    set_id: 'payment_type.v1',
    custom: false,
    // behavior.kind enum for payment_type:
    //   'manual'         — no automation; operator marks paid. Default for
    //                       cash/check where BIDIT doesn't broker the rail.
    //   'card'           — future: card-on-file / Stripe integration.
    //                       Params: { processor: 'stripe' | 'square' | ... }
    //   'bank_transfer'  — future: ACH/wire; requires routing + account on
    //                       customer. Params: { rail: 'ach' | 'wire' }
    values: [
      {
        value: 'cash',
        label: 'Cash',
        description: 'Physical currency, received in-person.',
        behavior: { kind: 'manual' },
      },
      {
        value: 'check',
        label: 'Check',
        description: 'Paper check, mailed or delivered in-person.',
        behavior: { kind: 'manual' },
      },
      {
        value: 'credit_card',
        label: 'Credit Card',
        description: 'Credit card payment — surcharge may apply per tenant policy.',
        behavior: { kind: 'card', processor: null },
      },
      {
        value: 'draw',
        label: 'Draw',
        description: 'Paid from a construction draw.',
        behavior: { kind: 'manual' },
      },
    ],
    // IDI-223: retired from the seed; labels kept (F3), never seeded.
    prior_seed: [
      {
        value: 'ach_transfer',
        label: 'ACH Transfer',
        description: 'Bank-to-bank electronic transfer. Requires customer routing + account numbers on file.',
        behavior: { kind: 'bank_transfer', rail: 'ach' },
      },
      {
        value: 'wire_transfer',
        label: 'Wire Transfer',
        description: 'Bank wire transfer. Requires beneficiary bank details on customer record.',
        behavior: { kind: 'bank_transfer', rail: 'wire' },
      },
      {
        value: 'other',
        label: 'Other',
        description: 'Payment method not otherwise categorized. Free-text note recommended.',
        behavior: { kind: 'manual' },
      },
    ],
    consumers: ['CustomerFormPanel.js', 'QuoteBuilder', 'QuoteReviewSend.js', 'QuoteDefaultsPage.js', '(future) PaymentProcessor'],
    note: 'Operator ruling Q2 (2026-07-21). Replaces prior seed which included PayPal/Zelle — those were consumer P2P rails and are not appropriate for a B2B commercial subcontractor app. Legacy stored `paypal`/`zelle` values render as tenant Quote Defaults `payment_type` (step 3 of render chain) or via prettifyEnum (step 4). PR-B2a: flipped custom:true → custom:false. PR-B2b-part-1: extended each row with `description` + `behavior: {kind, params}` schema. `behavior.kind` splits into manual (cash/check/other), card (future card processor integration), bank_transfer (ACH/wire, requires customer bank details). Enforcement not shipped yet — schema locks shape for downstream consumers.',
  },

  work_order_type: {
    set_id: 'work_order_type.v1',
    custom: true,
    values: [
      { value: 'blow',              label: 'Blow' },
      { value: 'installation',      label: 'Installation' },
      { value: 'prelim',            label: 'Prelim' },
      { value: 'repair',            label: 'Repair' },
      { value: 'rework',            label: 'Rework' },
      { value: 'spray_foam',        label: 'Spray Foam' },
    ],
    prior_seed: [
      { value: 'installation',      label: 'Installation' },
      { value: 'service_call',      label: 'Service Call' },
      { value: 'inspection',        label: 'Inspection' },
      { value: 'punch_list',        label: 'Punch List' },
      { value: 'warranty_callback', label: 'Warranty Callback' },
    ],
    consumers: ['JobWOTypesSettings.js', '(future WO surfaces)'],
    note: 'Operator ruling Q1 (2026-07-21). Chosen for field-services vernacular: preserves punch_list + warranty from prior seed (renamed to warranty_callback for clarity), replaces bare "service" with "service_call" to disambiguate from job_type "service", drops "maintenance" (not typical for project-trade subcontractors — tenants can add if needed).',
  },

  contact_role: {
    set_id: 'contact_role.v1',
    custom: true,
    values: [
      { value: 'bookkeeper',         label: 'Bookkeeper' },
      { value: 'general_contractor', label: 'General Contractor' },
      { value: 'office_manager',     label: 'Office Manager' },
      { value: 'owner',              label: 'Owner' },
      { value: 'superintendent',     label: 'Superintendent' },
    ],
    prior_seed: [
      { value: 'primary',           label: 'Primary' },
      { value: 'owner',             label: 'Owner' },
      { value: 'billing',           label: 'Billing' },
      { value: 'site_contact',      label: 'Site Contact' },
      { value: 'foreman',           label: 'Foreman' },
      { value: 'property_manager',  label: 'Property Manager' },
    ],
    consumers: ['CustomerFormPanel.js', 'QuoteReviewSend.js', 'CustomerTypesPage.js (PR-B2)'],
    note: 'Operator ruling A (2026-07-21). Retires hardcoded CONTACT_ROLE_OPTIONS in CustomerFormPanel (deletion happens in PR-B2). Same flat list applies across all customer types; NOT nested per-customer-type.',
  },

  customer_type: {
    set_id: 'customer_type.v1',
    custom: true,
    // PT-30 (2026-07-26): each row MAY carry `default_margin_pct` (Lindsey level 3
    // — Customer Type Margin). Stored as a decimal 0..1 (e.g. 0.30 = 30%), OR as
    // a percent 0..100 (both accepted — the QuoteBuilder's customer-type lookup
    // normalizes to decimal at read time). NULL/missing means "cascade to the
    // next Lindsey level" (customerTypeMargin → quoteMargin). Tenant-added types
    // via CustomerTypesPage may also carry this attribute. See
    // frontend/docs/pt-30/PRECEDENCE.md.
    values: [
      { value: 'commercial_new',       label: 'Commercial New',       default_margin_pct: null },
      { value: 'commercial_remodel',   label: 'Commercial Remodel',   default_margin_pct: null },
      { value: 'residential_new',      label: 'Residential New',      default_margin_pct: null },
      { value: 'residential_remodel',  label: 'Residential Remodel',  default_margin_pct: null },
    ],
    prior_seed: [
      { value: 'residential',          label: 'Residential' },
      { value: 'residential_remodel',  label: 'Residential Remodel' },
      { value: 'commercial',           label: 'Commercial' },
      { value: 'commercial_remodel',   label: 'Commercial Remodel' },
    ],
    consumers: ['CustomerFormPanel.js', 'CustomerBlock.js', 'QuoteReviewSend.js', 'CustomerTypesPage.js', 'QuoteBuilderPage.js (PT-30 customerTypeMargin resolver)'],
    note: 'Preserved from prior seed (VOCAB_SEEDS.md v1.3). Operators may add tenant-specific customer types via CustomerTypesPage. PT-30 (2026-07-26): each row may carry `default_margin_pct` (Lindsey level 3). Not seeded with values — tenants set their own defaults per stakeholder walkthrough ("a residential customer might be 30%").',
  },

  lead_source: {
    set_id: 'lead_source.v1',
    custom: true,
    values: [
      { value: 'internet',        label: 'Internet' },
      { value: 'referral',        label: 'Referral' },
      { value: 'repeat_customer', label: 'Repeat Customer' },
    ],
    prior_seed: [
      { value: 'referral',        label: 'Referral' },
      { value: 'repeat_customer', label: 'Repeat Customer' },
      { value: 'website',         label: 'Website' },
      { value: 'google',          label: 'Google' },
      { value: 'other',           label: 'Other' },
    ],
    consumers: ['CustomerFormPanel.js', 'LeadSourcesPage.js'],
    note: 'Operator ruling §4 (2026-07-21): trimmed from 8 → 5 essentials + Other. Dropped Yard Sign, Trade Show, Facebook — users add those as custom values if needed. Standing principle D-231: seeds are the starter set the stakeholders named, not an exhaustive taxonomy.',
  },

  job_type: {
    set_id: 'job_type.v1',
    custom: true,
    values: [
      { value: 'commercial_new',      label: 'Commercial New' },
      { value: 'commercial_remodel',  label: 'Commercial Remodel' },
      { value: 'residential_new',     label: 'Residential New' },
      { value: 'residential_remodel', label: 'Residential Remodel' },
      { value: 'commercial_ti',       label: 'Commercial TI' },
      { value: 'custom_home',         label: 'Custom Home' },
      { value: 'education',           label: 'Education' },
      { value: 'healthcare',          label: 'Healthcare' },
      { value: 'tract_house',         label: 'Tract House' },
      { value: 'warehouse',           label: 'Warehouse' },
    ],
    prior_seed: [
      { value: 'insulation',   label: 'Insulation' },
      { value: 'fireproofing', label: 'Fireproofing' },
      { value: 'firestopping', label: 'Firestopping' },
      { value: 'acoustical',   label: 'Acoustical' },
      { value: 'spray_foam',   label: 'Spray Foam' },
      { value: 'other',        label: 'Other' },
    ],
    consumers: ['QuoteHeader.js', 'JobsList (Dashboard, JobsPage, JobDetail)', 'CalendarPage.js', 'JobWOTypesSettings.js', 'reportDefinitions.js (Sales by Job Type)', 'MasterDefaults.js'],
    note: 'Operator ruling §1 (2026-07-21): investigation surfaced 43 hits across 15 files — job_type is NOT dormant. Adopted the 6-item trade-context canonical (matches QuoteHeader.JOB_TYPES_FALLBACK that was the de-facto authority pre-cleanup). Retires the divergent 5-item Go backend seed (Spray Foam Open/Closed Cell, Insulation Removal, Batt Install, Blown-In — pre-existing drift, closed in the same PR via the export chain). Retires the hardcoded JOB_TYPES_FALLBACK in QuoteHeader.js (now reads via useSettings.getOptions). Also retires the hardcoded filter enum in reportDefinitions.js:583 (aligned to canonical). Reports and dashboard render sites all now flow through useSettings.getEnumLabel for label resolution.',
  },

  lost_reason: {
    set_id: 'lost_reason.v1',
    custom: true,
    // IDI-223: no "Other" (provisional, co-UAT).
    values: [
      { value: 'better_price',           label: 'Better Price' },
      { value: 'better_service',         label: 'Better Service' },
      { value: 'products',               label: 'Products' },
      { value: 'relationship',           label: 'Relationship' },
      { value: 'scheduling',             label: 'Scheduling' },
      { value: 'test_practice',          label: 'Test/Practice' },
    ],
    prior_seed: [
      { value: 'better_price',           label: 'Better Price' },
      { value: 'timing_scheduling',      label: 'Timing / Scheduling' },
      { value: 'went_with_competitor',   label: 'Went with Competitor' },
      { value: 'project_cancelled',      label: 'Project Cancelled / Postponed' },
      { value: 'unresponsive',           label: 'Unresponsive / No Decision' },
      { value: 'other',                  label: 'Other' },
    ],
    consumers: ['(future lost-quote flow — Mark as Lost)'],
    note: 'Operator ruling §5 (2026-07-21): ship 5 canonical + Other. Mark-as-Lost requires a reason code, so an empty seed would break the first lost quote on a new tenant. Users add their own custom reasons via Settings. Standing principle D-231: seeds are the starter set the stakeholders named, not an exhaustive taxonomy.',
  },

  resource_category: {
    set_id: 'resource_category.v1',
    custom: true,
    values: [
      { value: 'equipment', label: 'Equipment' },
      { value: 'vehicles',  label: 'Vehicles'  },
      { value: 'tools',     label: 'Tools'     },
    ],
    // IDI-223: `values` above label the built-in groups (getEnumLabel); the
    // rows a new dealer starts with are `seed_rows`, all under Equipment.
    // seedDefaults.resourceCategories is exported from seed_rows; the group
    // rows it carried before are retired (BIDIT-written, hidden on migrated).
    seed_rows: [
      { value: 'batt_truck',     label: 'Batt Truck',     category: 'equipment' },
      { value: 'blow_truck',     label: 'Blow Truck',     category: 'equipment' },
      { value: 'ladder',         label: 'Ladder',         category: 'equipment' },
      { value: 'scaffold',       label: 'Scaffold',       category: 'equipment' },
      { value: 'spray_foam_rig', label: 'Spray Foam Rig', category: 'equipment' },
    ],
    prior_seed: [
      { value: 'equipment', label: 'Equipment' },
      { value: 'vehicles',  label: 'Vehicles'  },
      { value: 'tools',     label: 'Tools'     },
    ],
    consumers: ['ResourceTypesPage.js', 'Scheduler'],
    note: 'Operator ruling §6 (2026-07-21): resources are things the tenant CREATES — vehicles, tools, equipment (plus any custom). Restored Tools (a ladder is a tool, not equipment). Dropped Crew because a crew is NOT a resource — it is a collection of users + optional invited members, belongs to the crews/users concept. Alias mismatch to reconcile in the export script: seedDefaults.json uses key `resourceCategories`, SET_ID_ALIASES maps `resource_types` → `resource_category`, and label plurality (Vehicles vs Vehicle) matters for existing tenant data.',
  },

  // ---------------------------------------------------------------------------
  // Tenant-specific list-type sets — no canonical values; seeded EMPTY.
  // Operators add rows via the corresponding Settings page. Included here so
  // (a) the Seed Registry (PR-D.7) has a row for each — the *set exists*
  //     even if the default values don't — and
  // (b) getOptions() has a deterministic "empty list is valid" behavior
  //     rather than falling through to an undefined lookup.
  // ---------------------------------------------------------------------------

  competitor:  { set_id: 'competitor.v1',  custom: true, values: [], consumers: ['CompetitorsPage.js', 'QuoteBuilder'],  note: 'Tenant-specific.' },
  vendor:      { set_id: 'vendor.v1',      custom: true, values: [], consumers: ['VendorsWarehousesPage.js'],            note: 'Tenant-specific.' },
  warehouse:   { set_id: 'warehouse.v1',   custom: true, values: [], consumers: ['VendorsWarehousesPage.js'],            note: 'Tenant-specific.' },
  office:      { set_id: 'office.v1',      custom: true, values: [], consumers: ['MultiOfficePage.js', 'CompanyProfilePage.js'], note: 'Tenant-specific.' },
  // area.v1 — BIDIT-owned canonical scope areas (definition_home).
  //
  // Operator ruling 2026-07-21 (see docs/seed-registry-OPERATOR-RULINGS.md):
  // BIDIT defines the base areas + sub-areas. Tenants CANNOT remove or rename
  // BIDIT-defined areas/sub-areas and CANNOT mint new top-level areas. Tenants
  // CAN add custom sub-areas only, and only under an existing BIDIT area.
  // Tenant-added sub-areas live in list_type='sub_areas' as append-only rows,
  // each carrying a parent_area_id pointing at an area.v1 row.id.
  //
  // IDs are stable identifiers — DO NOT change once shipped (AccessoryPicker
  // and other consumers key off these strings, e.g. `accessoryScope`).
  //
  // Migrated from repos/bidit/bidit/frontend/src/quote-builder/seedCatalog.js:AREAS
  // (PR-B2b-part-3 2026-07-25). The seedCatalog re-export remains for backward
  // compat during the transition; consumers should read via useSettings.getAreas().
  area: {
    set_id: 'area.v1',
    custom: true,
    values: [
      { value: 'ceilings',    label: 'Ceilings', subAreas: [
        { value: 'accessible_spf',    label: 'Accessible - SPF' },
        { value: 'inaccessible',      label: 'Inaccessible' },
        { value: 'bay',               label: 'Bay' },
        { value: 'coffered',          label: 'Coffered' },
        { value: 'cathedral',         label: 'Cathedral' },
        { value: 'vaulted',           label: 'Vaulted' },
        { value: 'cold_floor',        label: 'Cold Floor' },
        { value: 'gcla',              label: 'GCLA' },
      ]},
      { value: 'floors',      label: 'Floors', subAreas: [
        { value: 'crawl',             label: 'Crawl' },
        { value: 'netted_from_above', label: 'Netted From Above' },
        { value: 'vacuuming_disposal',label: 'Insulation Vacuuming and Disposal' },
      ]},
      { value: 'walls',       label: 'Walls', subAreas: [
        { value: '2x6_basement',      label: '2x6 Basement Walls' },
        { value: 'exterior_open_closed', label: 'Exterior - Open/Closed' },
        { value: 'garage_exterior',   label: 'Garage Exterior Walls' },
        { value: 'interior',          label: 'Interior' },
        { value: 'knee_wall',         label: 'Knee Wall - Open Back' },
        { value: 'overhangs',         label: 'Overhangs/Cantilever/Soffits' },
        { value: 'rim_joists',        label: 'Rim Joists' },
      ]},
      { value: 'basement',    label: 'Basement',    subAreas: [] },
      { value: 'air_sealing', label: 'Air Sealing', subAreas: [] },
      { value: 'vent',        label: 'Vent',        subAreas: [] },
      { value: 'removal',     label: 'Removal',     subAreas: [] },
      { value: 'options',     label: 'Options',     subAreas: [] },
    ],
    // IDI-672: a new dealer's own rows at the built-in ids (IDI's areas,
    // Lindsey 08-31). Stored once, at a non-migrated dealer's first store; the
    // exporter puts them under `$schema`, so no stored record is backfilled.
    new_dealer_rows: {
      custom_areas: [
        { id: 'basement',    name: 'Basement',    active: false, isBiditOverride: true, subAreas: [] },
        { id: 'air_sealing', name: 'Air Sealing', active: false, isBiditOverride: true, subAreas: [] },
        { id: 'vent',        name: 'Vent',        active: false, isBiditOverride: true, subAreas: [] },
        { id: 'removal',     name: 'Removal',     active: false, isBiditOverride: true, subAreas: [] },
      ],
      subAreas: [
        { id: 'accessible_spf',       name: 'Accessible',               parent_area_id: 'ceilings', active: true },
        { id: 'vacuuming_disposal',   name: 'GCLA - Cold Floor',        parent_area_id: 'floors',   active: true },
        { id: '2x6_basement',         name: 'Basement Walls',           parent_area_id: 'walls',    active: true },
        { id: 'exterior_open_closed', name: 'Exterior - Opened/Closed', parent_area_id: 'walls',    active: true },
        { id: 'garage_exterior',      name: 'Garage Exterior Walls',    parent_area_id: 'walls',    active: false },
        { id: 'knee_wall',            name: 'Knee Wall - Open Back',    parent_area_id: 'walls',    active: false },
      ],
    },
    consumers: ['QuoteAreasPage.js', 'AddItemCascade.js', 'AccessoryPicker.js (areaScope)', 'seedCatalog.js (re-export shim)', 'MasterDefaults.js', 'useSettings.getAreas()'],
    note: 'BIDIT-owned canonical set (8 areas, 18 sub-areas). Migrated from quote-builder/seedCatalog.js:AREAS in PR-B2b-part-3 (2026-07-25). sub_area.v1 and quote_area.v1 were retired in the same PR — the previous 3-entry namespace fragmentation collapsed into this single authoritative entry with sub-areas nested inline. Tenant-added sub-areas do NOT live here; they persist to settings/sub_areas list_type as append-only rows with parent_area_id.',
  },
  tc_template: { set_id: 'tc_template.v1', custom: true, values: [], consumers: ['TCTemplatesPage.js', 'QuoteBuilder'],  note: 'Tenant-specific T&C templates.' },
  tax_rate:    { set_id: 'tax_rate.v1',    custom: true, values: [], consumers: ['TaxRatesPage.js', 'QuoteBuilder'],     note: 'Tenant-specific tax jurisdictions.' },
  // quote_area.v1 and sub_area.v1 retired in PR-B2b-part-3 (2026-07-25).
  // Collapsed into area.v1 above (sub-areas nested inline per row). Prior to
  // the collapse these existed as three overlapping empty stubs — see
  // .sherpa/tech-debt.md "Quote Areas governance defect" for the full history.
  // Legacy list_type='quote_areas' backend data (any tenant that hit the old
  // client-side seed useEffect) is handled by useSettings.getAreas() migration
  // shim; the underlying settings/quote_areas rows are drained on first read.

  // ---------------------------------------------------------------------------
  // Q6 registry additions (PR-B2b-part-1 2026-07-21).
  //
  // `user_role.v1` — closed set of roles a super-admin can assign to a user.
  // Each row carries a `description` + `permissions` starter bundle. Tenants
  // cannot add roles; only super-admin edits via UsersRolesPage / MasterDefaults.
  // Field Tech is the ONE role that cannot be tagged as a salesperson (see the
  // salesperson-picker consumer trace for the current implementation).
  //
  // `feature_flag.v1` — closed set of app-wide feature toggles. Super-admin-only
  // edit. Users have no direct control; feature flags gate whole surfaces or
  // behaviors (e.g., PDF export, calendar sync, invoice module). Empty for now
  // because no flags are actively defined; the set is registered so future flags
  // enter this regime by default.
  // ---------------------------------------------------------------------------

  user_role: {
    set_id: 'user_role.v1',
    custom: false,
    // ─── ROLE MODEL (operator ruling §7 2026-07-21, Option A) ────────────────
    //
    // Four roles. Two orthogonal dimensions:
    //   1. `can_sell` (boolean) — may this role be tagged as a salesperson
    //      on a customer / quote? Authoritative source for the salesperson-
    //      picker allow-list. Retires the fragile inclusion list
    //      `['owner','admin','editor','salesperson','sales_rep']` in
    //      CustomerFormPanel.js:258 + AddCustomerDrawer.js:74 (see ledger
    //      items on that migration).
    //   2. `tier` (enum: 'full_edit' | 'partial_edit' | 'view_only') — coarse
    //      permission bundle. Fine-grained per-user overrides live on the
    //      users table + Users & Roles "manage" CTA (separate concern,
    //      NOT a seed).
    //
    // SA-2 SOT_PACK reconciliation:
    //   The SA-2 role & permissions matrix in Iteration_18/SOT_PACK/
    //   ROLE_AND_PERMISSIONS_MATRIX.md was marked "PROPOSED — confirm with
    //   IDI" (5 roles, 16-row grouped permission checklist). Operator
    //   authorized 2026-07-21 to mark SA-2 SUPERSEDED and adopt this 4-role
    //   set as the canonical. See the header stamp on that pack file for
    //   the supersession record.
    values: [
      {
        value: 'admin',
        label: 'Admin',
        description: 'Full access to every app surface and setting. Can invite users, edit roles, configure the tenant. Can be tagged as a salesperson.',
        permissions: { tier: 'full_edit', can_sell: true },
      },
      {
        value: 'salesperson',
        label: 'Salesperson',
        description: 'Sells and manages assigned customers + quotes. Sees their own pipeline. Cannot manage users or edit tenant settings. Can be tagged as a salesperson (obviously).',
        permissions: { tier: 'partial_edit', can_sell: true },
      },
      {
        value: 'office_admin',
        label: 'Office Admin',
        description: 'Administrative work — customer records, scheduling, invoicing. Cannot manage users or edit tenant settings. Can be tagged as a salesperson.',
        permissions: { tier: 'partial_edit', can_sell: true },
      },
      {
        value: 'field_tech',
        label: 'Field Tech',
        description: 'On-site technician — sees assigned work orders, updates status, uploads photos. View-only for everything else. Cannot be tagged as a salesperson.',
        permissions: { tier: 'view_only', can_sell: false },
      },
    ],
    consumers: ['UsersRolesPage.js', 'CustomerFormPanel.js (salesperson-picker filter)', 'AddCustomerDrawer.js (salesperson-picker filter)', '(future) permission-tier enforcement'],
    note: 'Operator ruling §7 (2026-07-21) Option A: 4-role set adopted; SA-2 SOT_PACK (PROPOSED-unratified) marked SUPERSEDED. Closed set — super-admin only edits. `permissions.tier` is a coarse 3-level bundle (full_edit / partial_edit / view_only); fine-grained per-user overrides are a separate per-user concern, NOT a seed. `can_sell: true` rows are the authoritative salesperson-picker allow-list — replaces the fragile 5-value inclusion list currently used in CustomerFormPanel.js + AddCustomerDrawer.js (ledger item follow-up).',
  },

  feature_flag: {
    set_id: 'feature_flag.v1',
    custom: false,
    values: [], // No feature flags defined yet. When flags land, register them here first.
    consumers: ['(future) feature-flag consumers'],
    note: 'Q6 registry addition (2026-07-21). Closed set — super-admin only. Empty seed for now; new feature flags MUST register here first (single `definition_home`) before any consumer reads them. This keeps flags out of the "3 hardcoded booleans scattered across files" anti-pattern.',
  },

  // ---------------------------------------------------------------------------
  // NOT list-type sets — single-record settings; included here for Seed
  // Registry completeness (PR-D.7). These are edited via dedicated Settings
  // pages (MarginsPage, QuoteDefaultsPage, TCTemplatesPage) and not consumed
  // by getOptions().
  // ---------------------------------------------------------------------------

  margin_defaults: {
    set_id: 'margin_defaults.v1', custom: false,
    values: { material: 0.35, labor: 0.35, other: 0.20 },
    consumers: ['MarginsPage.js', 'QuoteBuilder margin engine', 'QuoteBuilder Cost Breakdown (Material / Labor / Other buckets)'],
    note: 'Operator ruling Q6a (2026-07-21): dropped `subcontractor` bucket. This contractor IS the subcontractor and does not typically sub-sub. The shape now aligns to the Cost Breakdown buckets (Material / Labor / Other) that the pricing model already uses across QuoteBuilder + Cost Breakdown reports. If sub-sub work becomes a real concern, add the bucket then.',
  },
  quote_defaults: {
    set_id: 'quote_defaults.v1', custom: false,
    values: { payment_terms: 'cod', payment_type: 'check', valid_until_days: 30, price_rounding: 'none' },
    consumers: ['QuoteDefaultsPage.js', 'useSettings.getEnumLabel (step 3 fallback)', 'QuoteBuilder new-quote prefill'],
    note: 'Single-record. The `payment_terms` and `payment_type` fields here ARE what step 3 of the render-precedence chain reads for its tenant-default fallback (see getEnumLabel).',
  },
};

// ---------------------------------------------------------------------------
// SET_ID_ALIASES — maps API list_type (plural) → vocabSeeds set key (singular)
// so both `getOptions('payment_types')` (from a settings page) and
// `getEnumLabel('payment_type', ...)` (from a render site) resolve to the same
// seed. Extend when new sets are added.
// ---------------------------------------------------------------------------
export const SET_ID_ALIASES = {
  payment_terms:    'payment_term',
  payment_types:    'payment_type',
  work_order_types: 'work_order_type',
  contact_roles:    'contact_role',
  customer_types:   'customer_type',
  lead_sources:     'lead_source',
  job_types:        'job_type',
  lost_reasons:     'lost_reason',
  resource_types:   'resource_category',
  competitors:      'competitor',
  vendors:          'vendor',
  warehouses:       'warehouse',
  offices:          'office',
  areas:            'area',
  // sub_areas alias intentionally omitted — PR-B2b-part-3 (2026-07-25) collapsed
  // sub_area.v1 into area.v1 (nested). Tenant-added sub-areas live in the
  // list_type='sub_areas' storage but have NO canonical seed; defaultsFor()
  // correctly returns [] for that list_type (pure tenant-authored).
  tc_templates:     'tc_template',
  tax_rates:        'tax_rate',
  // PR-B2b-part-3 (2026-07-25): quote_areas alias retired — the legacy `quote_area.v1`
  // seed was collapsed into area.v1. Callsites still passing list_type='quote_areas'
  // fall through resolveSetKey and yield defaultsFor → [] (no canonical fallback).
  // Preferred read paths: list_type='areas' (→ area.v1) for base areas, or
  // list_type='sub_areas' for tenant-added rows.
  // Q6 registry additions (PR-B2b-part-1 2026-07-21) — both are singular-form
  // by default (no plural inflection needed), but registered here for
  // discoverability + so `resolveSetKey` behavior is deterministic.
  user_roles:       'user_role',
  feature_flags:    'feature_flag',
};

/**
 * resolveSetKey(listTypeOrSetKey) — normalize any callsite input to the vocabSeeds set key.
 * Accepts either the plural list_type (as used by the backend + `useSettings`)
 * or the singular set key. Returns the singular set key, or the input unchanged
 * if no alias exists (allows forward-compat with sets added at runtime).
 */
export function resolveSetKey(listTypeOrSetKey) {
  if (!listTypeOrSetKey) return null;
  return SET_ID_ALIASES[listTypeOrSetKey] || listTypeOrSetKey;
}

/**
 * getSeedValues(listTypeOrSetKey) — canonical values for a set, normalized to
 * `{value, label}` shape. Returns [] if the set doesn't exist or is a
 * single-record (non-list) set.
 */
export function getSeedValues(listTypeOrSetKey) {
  const key = resolveSetKey(listTypeOrSetKey);
  const entry = VOCAB_SEEDS[key];
  if (!entry || !Array.isArray(entry.values)) return [];
  return entry.values.map(v =>
    typeof v === 'string' ? { value: v, label: v } : { value: v.value, label: v.label ?? v.value }
  );
}

/**
 * findSeedLabel(listTypeOrSetKey, value) — case-insensitive lookup of a single
 * value's label against the canonical seed. Returns null if not found.
 * Used by getEnumLabel step 2.
 */
export function findSeedLabel(listTypeOrSetKey, value) {
  if (value == null || value === '') return null;
  const key = resolveSetKey(listTypeOrSetKey);
  const entry = VOCAB_SEEDS[key];
  if (!entry || !Array.isArray(entry.values)) return null;
  const needle = String(value).toLowerCase();
  const isNeedle = v => String(typeof v === 'string' ? v : v.value).toLowerCase() === needle;
  // IDI-223 (F3): a value retired from the seed keeps its label.
  const match = entry.values.find(isNeedle) || (entry.prior_seed || []).find(isNeedle);
  if (!match) return null;
  return typeof match === 'string' ? match : (match.label ?? match.value);
}

/**
 * getVocabOptions(setKey, customValues) — merges canonical values with any
 * custom values added at runtime. PRESERVED from prior API (R17-07 / RF-17-7).
 * New code should prefer `useSettings().getOptions(listType)`.
 */
export function getVocabOptions(setKey, customValues = []) {
  const options = getSeedValues(setKey);
  const entry = VOCAB_SEEDS[resolveSetKey(setKey)];
  if (entry?.custom && customValues.length) {
    const seedSet = new Set(options.map(o => o.value.toLowerCase()));
    customValues.forEach(cv => {
      const cvVal = typeof cv === 'string' ? cv : cv.value;
      const cvLabel = typeof cv === 'string' ? cv : (cv.label ?? cv.value);
      if (!seedSet.has(String(cvVal).toLowerCase())) {
        options.push({ value: cvVal, label: cvLabel });
      }
    });
  }
  return options;
}

/**
 * prettifyEnum(rawValue) — step-4 cosmetic fallback used by getEnumLabel when
 * no other resolution succeeds. Kept here (not in useSettings) so anything
 * needing the same behavior in a non-hook context can import it directly.
 *
 * 'net_30' → 'Net 30'  ·  'cash' → 'Cash'  ·  'ach_transfer' → 'Ach Transfer'
 */
export function prettifyEnum(value) {
  if (value == null || value === '') return '';
  return String(value)
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export default VOCAB_SEEDS;
